import { useEffect } from 'react'
import { IAlarm, IImage, IPassport } from '@obvious.tech/constellation'
import { Entity } from '@obvious.tech/constellation/Entity'

import api, { TApiResponse, TEntity, TUseApiActionState } from 'src/utils/api'
import {
  getAssignedPassportIds,
  selectLogoImageId,
  toImageSource,
  TPassportConfiguration,
} from './clientLogo'

const getPassportByID = api.getResourceByID<TEntity<IPassport>>(
  Entity.getResource('Passport'),
)

const getPassportConfigurations = api.fetchResource<TPassportConfiguration[]>(
  Entity.getResource('PassportConfiguration'),
)

const getImageByID = api.getResourceByID<TEntity<IImage>>(
  Entity.getResource('Image'),
)

type TOptions = {
  alarm: TEntity<IAlarm>
  token?: string
}

const resolveClientLogo = async ({
  alarm,
  token,
}: TOptions): Promise<TApiResponse<string | null>> => {
  const passportIds = getAssignedPassportIds(alarm)
  if (passportIds.length === 0)
    return {
      _tag: 'success',
      data: null,
    }

  const [passportConfigurations, passports] = await Promise.all([
    getPassportConfigurations({
      token,
    }),
    Promise.all(
      passportIds.map(async id => await getPassportByID({ id, token })),
    ),
  ])

  if (passportConfigurations._tag === 'failure') return passportConfigurations

  let imageId: string | null = null

  for (const [index, passportId] of passportIds.entries()) {
    const passport = passports[index]
    if (passport === undefined || passport._tag === 'failure') continue

    imageId = selectLogoImageId({
      passportId,
      passportGroups: passport.data.groups ?? [],
      passportConfigurations: passportConfigurations.data,
    })
    if (imageId !== null) break
  }

  if (imageId === null)
    return {
      _tag: 'success',
      data: null,
    }

  const image = await getImageByID({
    id: imageId,
    token,
  })
  if (image._tag === 'failure') return image

  const source = toImageSource(image.data.imageData)
  if (source === null)
    return {
      _tag: 'failure',
      error: new Error(
        `Logo Image ${imageId} is not a PNG or a JPEG, the only formats a PDF report can embed.`,
      ),
    }

  return {
    _tag: 'success',
    data: source,
  }
}
export const getClientLogo = async (
  options: TOptions,
): Promise<TApiResponse<string | null>> => {
  try {
    return await resolveClientLogo(options)
  } catch (error) {
    return {
      _tag: 'failure',
      error:
        error instanceof Error
          ? error
          : new Error('Failed to resolve the client logo.', { cause: error }),
    }
  }
}

export type TUseClientLogoResult = TUseApiActionState<typeof getClientLogo>

export type TProps = {
  alarm: TEntity<IAlarm>
  authorization?: string
}

const useClientLogo = ({
  alarm,
  authorization,
}: TProps): TUseClientLogoResult => {
  const [clientLogo, getLogo] = api.useApiAction(getClientLogo)

  useEffect(() => {
    return getLogo({
      alarm,
      token: authorization,
    })
  }, [alarm, authorization, getLogo])

  return clientLogo
}

export default useClientLogo
